import { useEffect, useState } from "react";
import { Copy, KeyRound, Package, ShieldAlert, Trash2, Upload, UserPlus } from "lucide-react";
import PageLayout from "../components/PageLayout";
import { Admin } from "../lib/api";
import type { AdminUser } from "../lib/types";
import { useAuth } from "../contexts/AuthContext";

type Release = Awaited<ReturnType<typeof Admin.releases>>[number];

export default function AdminPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [activity, setActivity] = useState<Awaited<ReturnType<typeof Admin.activity>>>([]);
  const [releases, setReleases] = useState<Release[]>([]);
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [creds, setCreds] = useState<{ username: string; password: string } | null>(null);
  const [tab, setTab] = useState<"users" | "activity" | "releases">("users");

  const reload = async () => {
    setUsers(await Admin.users());
    setActivity(await Admin.activity(200));
    try {
      setReleases(await Admin.releases());
    } catch {
      setReleases([]);
    }
  };
  useEffect(() => { reload(); }, []);

  return (
    <PageLayout>
      <div className="px-4 md:px-8 py-4 max-w-5xl">
        <h1 className="font-display text-[36px] sm:text-[44px] tracking-[-0.5px] text-text-primary mb-2">
          Admin
        </h1>
        <p className="text-[12px] font-ui text-text-secondary mb-6">Manage users and monitor activity.</p>

        <div className="flex flex-wrap items-center gap-2 mb-6">
          {(["users", "activity", "releases"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-pill text-[12px] font-ui font-medium uppercase tracking-[0.04em] transition-fast border ${
                tab === t ? "bg-accent-gold text-canvas border-accent-gold" : "border-muted/40 text-text-secondary hover:text-text-primary"
              }`}
            >
              {t === "users" ? "Users" : t === "activity" ? "Activity log" : "TV app releases"}
            </button>
          ))}
          <div className="hidden sm:block flex-1" />
          {tab === "users" && (
            <button
              onClick={() => setAdding(true)}
              className="px-4 py-2 rounded-pill bg-accent-gold text-canvas text-[12px] font-ui font-medium uppercase tracking-[0.04em] hover:bg-accent-gold-light transition-fast inline-flex items-center gap-1.5"
            >
              <UserPlus size={14} /> Add user
            </button>
          )}
          {tab === "releases" && (
            <button
              onClick={() => setUploading(true)}
              className="px-4 py-2 rounded-pill bg-accent-gold text-canvas text-[12px] font-ui font-medium uppercase tracking-[0.04em] hover:bg-accent-gold-light transition-fast inline-flex items-center gap-1.5"
            >
              <Upload size={14} /> Upload APK
            </button>
          )}
        </div>

        {tab === "releases" ? (
          <ReleasesTab releases={releases} onChanged={reload} />
        ) : tab === "users" ? (
          <div className="overflow-x-auto bg-surface rounded-xl border border-muted/30">
            <table className="min-w-max w-full text-[13px] font-ui">
              <thead className="text-text-secondary uppercase tracking-[0.04em] text-[11px]">
                <tr>
                  <Th>Username</Th>
                  <Th>Role</Th>
                  <Th>State</Th>
                  <Th>Playlists</Th>
                  <Th>Last login</Th>
                  <Th align="right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-muted/20">
                    <Td>{u.username}</Td>
                    <Td>{u.is_admin ? <span className="text-accent-gold">Admin</span> : "Viewer"}</Td>
                    <Td>
                      {u.disabled ? (
                        <span className="text-danger inline-flex items-center gap-1"><ShieldAlert size={12} /> Disabled</span>
                      ) : u.must_change_password ? (
                        <span className="text-accent-gold">Must change pw</span>
                      ) : (
                        <span className="text-success">Active</span>
                      )}
                    </Td>
                    <Td>{u.playlist_count}</Td>
                    <Td>{u.last_login_at || "—"}</Td>
                    <Td align="right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={async () => {
                            if (!confirm(`Reset password for ${u.username}?`)) return;
                            const r = await Admin.resetPassword(u.id);
                            setCreds({ username: r.username, password: r.new_password });
                            reload();
                          }}
                          className="p-2 rounded-lg text-text-secondary hover:text-accent-gold transition-fast"
                          title="Reset password"
                        >
                          <KeyRound size={14} />
                        </button>
                        {u.id !== me?.id && (
                          <button
                            onClick={async () => {
                              if (!confirm(`Delete user ${u.username}?`)) return;
                              await Admin.deleteUser(u.id);
                              reload();
                            }}
                            className="p-2 rounded-lg text-text-secondary hover:text-danger transition-fast"
                            title="Delete user"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto bg-surface rounded-xl border border-muted/30">
            <table className="min-w-max w-full text-[12px] font-ui">
              <thead className="text-text-secondary uppercase tracking-[0.04em] text-[11px]">
                <tr>
                  <Th>Time</Th>
                  <Th>Action</Th>
                  <Th>User</Th>
                  <Th>IP</Th>
                  <Th>Detail</Th>
                </tr>
              </thead>
              <tbody>
                {activity.map((a) => (
                  <tr key={a.id} className="border-t border-muted/20">
                    <Td><span className="font-mono text-[11px] text-text-secondary">{a.ts}</span></Td>
                    <Td><span className="font-mono text-[11px] text-accent-gold">{a.action}</span></Td>
                    <Td>{a.username || "—"}</Td>
                    <Td>{a.ip || "—"}</Td>
                    <Td><span className="font-mono text-[11px] text-text-secondary">{a.detail || "—"}</span></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {adding && <AddUserModal onClose={() => setAdding(false)} onCreated={(c) => { setAdding(false); setCreds(c); reload(); }} />}
        {creds && <CredsModal creds={creds} onClose={() => setCreds(null)} />}
        {uploading && (
          <UploadReleaseModal
            onClose={() => setUploading(false)}
            onUploaded={() => { setUploading(false); reload(); }}
          />
        )}
      </div>
    </PageLayout>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: "right" }) {
  return <th className={`px-3 py-3 text-left ${align === "right" ? "text-right" : ""}`}>{children}</th>;
}
function Td({ children, align }: { children: React.ReactNode; align?: "right" }) {
  return <td className={`px-3 py-2.5 ${align === "right" ? "text-right" : ""}`}>{children}</td>;
}

function AddUserModal({ onClose, onCreated }: { onClose: () => void; onCreated: (c: { username: string; password: string }) => void }) {
  const [username, setUsername] = useState("");
  const [admin, setAdmin] = useState(false);
  const [pw, setPw] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await Admin.createUser(username.trim(), admin, pw || undefined);
      onCreated({ username: r.username, password: r.initial_password });
    } catch (e: unknown) {
      setErr((e as { response?: { data?: { detail?: string } } }).response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-canvas/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-[420px] glass-panel rounded-xl p-6">
        <h2 className="font-display text-[28px] tracking-[-0.5px] text-text-primary mb-4">Create user</h2>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-[11px] font-ui font-medium uppercase tracking-[0.06em] text-text-secondary mb-1">Username</label>
            <input autoFocus value={username} onChange={(e) => setUsername(e.target.value)} className="w-full h-10 bg-surface border border-muted/40 rounded-lg px-3 text-[14px] font-ui text-text-primary gold-focus transition-fast" />
          </div>
          <div>
            <label className="block text-[11px] font-ui font-medium uppercase tracking-[0.06em] text-text-secondary mb-1">Initial password (blank = auto-generate)</label>
            <input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="auto-generate" className="w-full h-10 bg-surface border border-muted/40 rounded-lg px-3 text-[14px] font-ui text-text-primary gold-focus transition-fast" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={admin} onChange={(e) => setAdmin(e.target.checked)} className="accent-accent-gold" />
            <span className="text-[13px] font-ui text-text-secondary">Make this user an administrator</span>
          </label>
          {err && <p className="text-[12px] font-ui text-danger">{err}</p>}
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-11 rounded-xl border border-muted/40 text-[13px] font-ui font-medium uppercase tracking-[0.04em] text-text-secondary">Cancel</button>
            <button type="submit" disabled={busy} className="flex-1 h-11 rounded-xl bg-accent-gold text-canvas text-[13px] font-ui font-medium uppercase tracking-[0.04em] disabled:opacity-50">{busy ? "Creating…" : "Create"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CredsModal({ creds, onClose }: { creds: { username: string; password: string }; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-canvas/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-[420px] glass-panel rounded-xl p-6">
        <h2 className="font-display text-[28px] tracking-[-0.5px] text-text-primary mb-2">One-time credentials</h2>
        <p className="text-[12px] font-ui text-text-secondary mb-4">
          Share these with the user. They'll be forced to change the password on first sign-in.
          This is the only time we'll show this password.
        </p>
        <div className="bg-surface rounded-xl p-4 border border-accent-gold/30 mb-4">
          <Row k="Username" v={creds.username} />
          <Row k="Password" v={creds.password} mono />
        </div>
        <button
          onClick={() => navigator.clipboard.writeText(`username: ${creds.username}\npassword: ${creds.password}`)}
          className="w-full h-11 rounded-xl border border-muted/40 text-[13px] font-ui font-medium uppercase tracking-[0.04em] text-text-primary hover:border-accent-gold transition-fast inline-flex items-center justify-center gap-2"
        >
          <Copy size={14} /> Copy to clipboard
        </button>
        <button onClick={onClose} className="w-full h-11 mt-2 rounded-xl bg-accent-gold text-canvas text-[13px] font-ui font-medium uppercase tracking-[0.04em] hover:bg-accent-gold-light transition-fast">
          Done
        </button>
      </div>
    </div>
  );
}

function Row({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex justify-between items-center py-1.5">
      <span className="text-[11px] font-ui font-medium uppercase tracking-[0.06em] text-text-secondary">{k}</span>
      <span className={`text-[14px] text-text-primary ${mono ? "font-mono" : "font-ui"}`}>{v}</span>
    </div>
  );
}

function ReleasesTab({ releases, onChanged }: { releases: Release[]; onChanged: () => void }) {
  if (releases.length === 0) {
    return (
      <div className="bg-surface rounded-xl border border-muted/30 p-10 text-center">
        <Package size={32} className="mx-auto mb-3 text-text-muted" />
        <p className="text-[14px] font-ui text-text-secondary mb-1">No TV-app releases uploaded yet.</p>
        <p className="text-[12px] font-ui text-text-muted">
          Upload a signed APK above. The TV app polls <span className="font-mono">/api/app/latest</span> on
          launch and prompts users to install when a higher versionCode is published.
        </p>
      </div>
    );
  }
  const fmtSize = (n: number) => {
    const mb = n / 1024 / 1024;
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(n / 1024).toFixed(0)} KB`;
  };
  return (
    <div className="overflow-x-auto bg-surface rounded-xl border border-muted/30">
      <table className="min-w-max w-full text-[13px] font-ui">
        <thead className="text-text-secondary uppercase tracking-[0.04em] text-[11px]">
          <tr>
            <Th>Version</Th>
            <Th>Code</Th>
            <Th>Size</Th>
            <Th>Uploaded</Th>
            <Th>Notes</Th>
            <Th align="right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {releases.map((r) => (
            <tr key={r.version_code} className="border-t border-muted/20">
              <Td>
                <span className="text-accent-gold">{r.version_name}</span>
              </Td>
              <Td>
                <span className="font-mono text-[11px] text-text-secondary">{r.version_code}</span>
              </Td>
              <Td>{fmtSize(r.size_bytes)}</Td>
              <Td>
                <span className="font-mono text-[11px] text-text-secondary">{r.uploaded_at}</span>
              </Td>
              <Td>
                <span className="text-text-secondary">{r.notes || "—"}</span>
              </Td>
              <Td align="right">
                <button
                  onClick={async () => {
                    if (!confirm(`Delete release ${r.version_name} (code ${r.version_code})?`)) return;
                    await Admin.deleteRelease(r.version_code);
                    onChanged();
                  }}
                  className="p-2 rounded-lg text-text-secondary hover:text-danger transition-fast"
                  title="Delete release"
                >
                  <Trash2 size={14} />
                </button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UploadReleaseModal({
  onClose,
  onUploaded,
}: {
  onClose: () => void;
  onUploaded: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [versionCode, setVersionCode] = useState("");
  const [versionName, setVersionName] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!file) {
      setErr("Pick an APK first");
      return;
    }
    const vc = parseInt(versionCode, 10);
    if (!Number.isFinite(vc) || vc <= 0) {
      setErr("versionCode must be a positive integer");
      return;
    }
    if (!versionName.trim()) {
      setErr("versionName required");
      return;
    }
    setBusy(true);
    setProgress(0);
    try {
      await Admin.uploadRelease(file, vc, versionName.trim(), notes.trim(), setProgress);
      onUploaded();
    } catch (e: unknown) {
      setErr(
        (e as { response?: { data?: { detail?: string } } }).response?.data?.detail ||
          "Upload failed",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-canvas/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => !busy && onClose()}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[520px] glass-panel rounded-xl p-6"
      >
        <h2 className="font-display text-[28px] tracking-[-0.5px] text-text-primary mb-1">
          Upload TV-app release
        </h2>
        <p className="text-[12px] font-ui text-text-secondary mb-4">
          Pick a signed APK and tag it with versionCode + versionName from <span className="font-mono">app/build.gradle.kts</span>.
          Already-installed clients will see "Update available" on next launch when versionCode is higher.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-[11px] font-ui font-medium uppercase tracking-[0.06em] text-text-secondary mb-1">
              APK file
            </label>
            <input
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-[13px] font-ui text-text-primary file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-accent-gold file:text-canvas file:text-[12px] file:font-ui file:font-medium file:cursor-pointer"
            />
            {file && (
              <p className="mt-1 text-[11px] font-mono text-text-muted">
                {file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-ui font-medium uppercase tracking-[0.06em] text-text-secondary mb-1">
                versionCode
              </label>
              <input
                value={versionCode}
                onChange={(e) => setVersionCode(e.target.value)}
                inputMode="numeric"
                placeholder="e.g. 5"
                className="w-full h-10 bg-surface border border-muted/40 rounded-lg px-3 text-[14px] font-mono text-text-primary gold-focus transition-fast"
              />
            </div>
            <div>
              <label className="block text-[11px] font-ui font-medium uppercase tracking-[0.06em] text-text-secondary mb-1">
                versionName
              </label>
              <input
                value={versionName}
                onChange={(e) => setVersionName(e.target.value)}
                placeholder="e.g. 0.4.0"
                className="w-full h-10 bg-surface border border-muted/40 rounded-lg px-3 text-[14px] font-ui text-text-primary gold-focus transition-fast"
              />
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-ui font-medium uppercase tracking-[0.06em] text-text-secondary mb-1">
              Release notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="What changed in this build…"
              className="w-full bg-surface border border-muted/40 rounded-lg px-3 py-2 text-[13px] font-ui text-text-primary gold-focus transition-fast"
            />
          </div>
          {err && <p className="text-[12px] font-ui text-danger">{err}</p>}
          {busy && progress > 0 && (
            <div className="w-full h-2 bg-surface rounded-full overflow-hidden">
              <div
                className="h-full bg-accent-gold transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="flex-1 h-11 rounded-xl border border-muted/40 text-[13px] font-ui font-medium uppercase tracking-[0.04em] text-text-secondary disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="flex-1 h-11 rounded-xl bg-accent-gold text-canvas text-[13px] font-ui font-medium uppercase tracking-[0.04em] disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
            >
              <Upload size={14} /> {busy ? `Uploading ${progress}%` : "Upload"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
